import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from './i18n/useLanguage';
import { supportCopy } from './i18n/support';
import { legalCopy } from './i18n/legal';
import { trackEvent } from './analytics';
import PageMetadata from './PageMetadata';
import SectionLabel from './SectionLabel';
import { SiteHeader, SiteFooter, IconSprite, Icon, Arrow } from './SiteChrome';
import './LegalPages.css';
import './SupportPage.css';

const SUPPORT_EMAIL = 'support@plannely.app';
const FAQ_DELETE_INDEX = 4;
const FAQ_PRIVACY_INDEX = 6;
const faqId = index => (index === FAQ_DELETE_INDEX ? 'faq-delete' : 'faq-' + (index + 1));

function openFaqFromHash() {
  const id = window.location.hash.slice(1);
  if (!id.startsWith('faq-')) return;
  const item = document.getElementById(id);
  if (item && item.tagName === 'DETAILS') item.open = true;
}

export default function SupportPage() {
  const [language, changeLanguage] = useLanguage();
  const copy = supportCopy(language);
  const common = legalCopy(language);
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef(0);
  const mailto = 'mailto:' + SUPPORT_EMAIL + '?subject=' + encodeURIComponent(copy.contact.subject);
  const canCopy = typeof navigator !== 'undefined' && Boolean(navigator.clipboard);

  useEffect(() => {
    openFaqFromHash();
    window.addEventListener('hashchange', openFaqFromHash);
    return () => { window.removeEventListener('hashchange', openFaqFromHash); window.clearTimeout(resetTimer.current); };
  }, []);

  const writeClick = () => trackEvent('support_email_click', { language });
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      window.clearTimeout(resetTimer.current);
      resetTimer.current = window.setTimeout(() => setCopied(false), 2200);
      trackEvent('support_email_copy', { language });
    } catch {
      // The address stays visible and selectable when the clipboard is blocked.
    }
  };

  const topicActions = [
    <>
      <a className="support-pill" href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noopener noreferrer"><Icon name="apple" />App Store</a>
      <a className="support-pill" href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noopener noreferrer"><Icon name="google" />Google Play</a>
    </>,
    <a className="support-link" href={'#' + faqId(FAQ_DELETE_INDEX)}>{copy.cards[1].cta}<Arrow /></a>,
    <a className="support-link" href="/privacy-policy">{copy.cards[2].cta}<Arrow /></a>,
  ];

  return <div className="plannely-layout">
    <PageMetadata language={language} title={copy.meta.title} description={copy.meta.description} url="https://plannely.app/support" />
    <IconSprite />
    <div className="wrap" id="top">
      <SiteHeader language={language} onLanguageChange={changeLanguage} />
      <main className="support-main">
        <header className="support-hero">
          <div>
            <SectionLabel>{copy.eyebrow}</SectionLabel>
            <h1>{copy.title}</h1>
            <p className="support-lead">{copy.lead}</p>
          </div>
          <aside className="support-card">
            <p className={'support-card-label' + (copied ? ' is-done' : '')} role="status" aria-live="polite">{copied ? copy.contact.copied : copy.contact.label}</p>
            <div className="support-email-row">
              <a className="support-email" href={mailto} onClick={writeClick}><Icon name="mail" />{SUPPORT_EMAIL}</a>
              {canCopy && <button type="button" className="support-copy" onClick={copyAddress} aria-label={copy.contact.copy} title={copy.contact.copy}><Icon name={copied ? 'check' : 'copy'} /></button>}
            </div>
            <a className="btn" href={mailto} onClick={writeClick}>{copy.contact.write}<Arrow /></a>
            <div className="support-include">
              <h2>{copy.include.title}</h2>
              <ul>{copy.include.items.map(item => <li key={item}><Icon name="check" />{item}</li>)}</ul>
            </div>
          </aside>
        </header>

        <section className="support-topics">
          {[['card', 0], ['trash', 1], ['shield', 2]].map(([icon, index]) => <article className="support-topic" key={icon}>
            <Icon name={icon} />
            <h2>{copy.cards[index].title}</h2>
            <p>{copy.cards[index].copy}</p>
            <div className="support-topic-actions">{topicActions[index]}</div>
          </article>)}
        </section>

        <section className="support-faq" id="faq">
          <div className="support-faq-head"><SectionLabel>{copy.faq.eyebrow}</SectionLabel><h2>{copy.faq.title}</h2></div>
          <div className="support-faq-list">
            {copy.faq.items.map((item, index) => <details key={item.question} id={faqId(index)} open={index === 0 ? true : undefined}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
              {index === FAQ_PRIVACY_INDEX && <a className="support-link" href="/privacy-policy">{copy.cards[2].cta}<Arrow /></a>}
            </details>)}
          </div>
        </section>

        <section className="support-band">
          <div><h2>{copy.band.title}</h2><p>{copy.band.copy}</p></div>
          <a className="btn light" href={mailto} onClick={writeClick}>{copy.contact.write}<Arrow /></a>
        </section>
        <a className="legal-back" href="/"><Arrow />{common.back}</a>
      </main>
      <SiteFooter language={language} currentPage="support" />
    </div>
  </div>;
}
